import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { NavItem, NavItemLogout } from './containers/NavItem';
import styles from './SideBar.module.scss';
import clx from 'classnames';
import useWindowSize from '../../utils/useWindowSize';
import { getActiveSalon } from 'Containers/Auth/store/selectors';
import { useSelector, useDispatch } from 'react-redux';
import { SUBSECTIONS } from 'Containers/Settings/containers/AccountsManager/constants';
import { getMyAccountStaff } from 'Containers/Settings/store/selectors';
import { useNavBarRoutes } from './constants';
import { useTranslation } from 'react-i18next';
import LanguageSelectorModal from '../Common/LanguageSelectorModal';
import { getCustomerProducts } from 'Containers/Settings/store/selectors';
import { ROUTES } from 'routes/constants';
import { PREMIUM_UPGRADE_PATH } from 'utils/premiumUpgradePath';
import { hasShopAccess } from 'utils/hasShopAccess';
import { sendMessageToApp } from 'utils/exsternalCall';
import SalonSelector from './containers/SalonSelector';
import AvatarIcon from 'Containers/Common/AvatarIcon';
import { modalActions } from 'Containers/Modals/store';
import useIsPremiumUnlocked from 'utils/useIsPremiumUnlocked';
import { isPathAllowed } from 'utils/staffPermissions';
import { getIconUrl, imageEntities } from 'utils/ImagesCreator';
import { DESKTOP_SIDEBAR_TOGGLE_EVENT } from './sidebarEvents';
import {
  HOME_NAV_NEW_SEEN_KEY,
  readLocalFlag,
  writeLocalFlag,
} from 'Containers/Home/utils/homePromoStorage';

const selectActiveSalon = getActiveSalon();
const selectMyAccountStaff = getMyAccountStaff();
const selectCustomerProducts = getCustomerProducts();

const filterGroupItems = (groups, predicate) =>
  groups
    .map((group) => ({
      ...group,
      items: (group.items || []).filter(predicate),
    }))
    .filter((group) => group.items.length > 0);

const SideBar = ({ mobileOpen = false, setMobileOpen, isCalendar = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams({ logout: 0 });
  const { i18n, t } = useTranslation();
  const NAV_BAR_GROUPS = useNavBarRoutes();
  const [activeItem, setActiveItem] = useState('');
  const { isMobile } = useWindowSize();
  // Desktop: stato locale → toggle non tocca PageWrapper/Header/contenuto.
  const [desktopOpen, setDesktopOpen] = useState(false);
  const activeSalon = useSelector(selectActiveSalon);
  const myAccount = useSelector(selectMyAccountStaff);
  const products = useSelector(selectCustomerProducts);
  const [permissions, setPermissions] = useState([]);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const forceShowShop = Number(activeSalon?.idsalone) === 2;
  const showShop =
    forceShowShop ||
    hasShopAccess(products) ||
    hasShopAccess(activeSalon) ||
    localStorage.getItem('has_shop') === 'true';
  const isStaff = Boolean(localStorage.getItem('staff'));
  const expensesPath = `${ROUTES.SETTINGS}${ROUTES.EXPENSES}`;
  const staffChairId = myAccount?.poltrona?.idpoltrona ?? myAccount?.poltrona;
  const staffChairName = myAccount?.nome_referente || '';
  const staffChairIcon = getIconUrl(staffChairId, imageEntities.worker);
  const isPremiumUnlocked = useIsPremiumUnlocked();
  const [homeNewSeen, setHomeNewSeen] = useState(() => readLocalFlag(HOME_NAV_NEW_SEEN_KEY));

  const isExpanded = isMobile ? mobileOpen : desktopOpen;

  const homePathRef = useRef(location.pathname);

  useEffect(() => {
    const onHome =
      location.pathname === ROUTES.HOME || location.pathname.startsWith(`${ROUTES.HOME}/`);
    const openedHome = homePathRef.current !== location.pathname && onHome;
    homePathRef.current = location.pathname;
    if (!openedHome) return;
    writeLocalFlag(HOME_NAV_NEW_SEEN_KEY);
    setHomeNewSeen(true);
  }, [location.pathname]);

  const goToUpgrade = useCallback(() => {
    navigate(PREMIUM_UPGRADE_PATH);
  }, [navigate]);

  const closeMobile = useCallback(() => {
    setMobileOpen?.(false);
  }, [setMobileOpen]);

  const toggleSideBar = useCallback(
    (e) => {
      e.stopPropagation();
      if (isMobile) {
        setMobileOpen?.((open) => !open);
        return;
      }
      setDesktopOpen((open) => !open);
    },
    [isMobile, setMobileOpen],
  );

  const languageGroups = useMemo(
    () =>
      filterGroupItems(NAV_BAR_GROUPS, (route) =>
        (route.languages || []).includes(i18n.language),
      ),
    [NAV_BAR_GROUPS, i18n.language],
  );

  const visibleNavGroups = useMemo(() => {
    let groups = filterGroupItems(languageGroups, (route) => {
      if (route.link === ROUTES.SHOP && !showShop) return false;
      return true;
    });

    if (isStaff) {
      groups = filterGroupItems(groups, (route) => {
        if (permissions.includes(route.link)) return true;
        // Staff con accesso Impostazioni vede anche Spese (gate Premium resta sul click).
        if (route.link === expensesPath && permissions.includes(ROUTES.SETTINGS)) {
          return true;
        }
        return false;
      });
    }

    return groups;
  }, [languageGroups, showShop, isStaff, permissions, expensesPath]);

  useEffect(() => {
    if (!isMobile) setDesktopOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    if (location) {
      setActiveItem(location.pathname);
    }

    if (myAccount.permessi?.length && localStorage.getItem('staff')) {
      let allowed = myAccount.permessi.map((perm) => {
        let sub = SUBSECTIONS.find((s) => s.id === perm.sezione);
        return sub?.link || '';
      });
      setPermissions(allowed);
    }
  }, [location, myAccount.permessi]);

  useEffect(() => {
    if (!localStorage.getItem('staff')) return;
    if (!permissions.length) return;

    const canAccessCurrent =
      isPathAllowed(location.pathname, permissions) ||
      (location.pathname === expensesPath && permissions.includes(ROUTES.SETTINGS)) ||
      location.pathname === PREMIUM_UPGRADE_PATH ||
      location.pathname.startsWith(`${PREMIUM_UPGRADE_PATH}/`);
    if (!canAccessCurrent) {
      navigate(permissions[0]);
    }
  }, [permissions, myAccount.permessi, location.pathname, navigate, expensesPath]);

  useEffect(() => {
    if (isMobile) return undefined;
    const onToggle = () => setDesktopOpen((open) => !open);
    window.addEventListener(DESKTOP_SIDEBAR_TOGGLE_EVENT, onToggle);
    return () => window.removeEventListener(DESKTOP_SIDEBAR_TOGGLE_EVENT, onToggle);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !mobileOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMobileOpen?.(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobile, mobileOpen, setMobileOpen]);

  // Chiudi overlay desktop con Esc / click fuori senza toccare il contenuto.
  useEffect(() => {
    if (isMobile || !desktopOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setDesktopOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobile, desktopOpen]);

  return (
    <aside
      id="app-sidebar"
      data-nav-expanded={isExpanded ? 'true' : 'false'}
      className={clx(styles.sidebar__wrapper, {
        [styles.sidebar__wrapper_open]: isExpanded && !isMobile,
        [styles.sidebar__wrapper__close_mobile]: !mobileOpen && isMobile,
        [styles.sidebar__wrapper__mobile]: isMobile,
        [styles.sidebar__wrapper__mobile_calendar]: isMobile && isCalendar,
        [styles.sidebar__wrapper__mobile_open]: isMobile && mobileOpen,
      })}
    >
      {isMobile ? (
        <div
          className={styles.sidebar__overlay}
          onClick={() => setMobileOpen?.(false)}
          aria-hidden={!mobileOpen}
        />
      ) : desktopOpen ? (
        <button
          type="button"
          className={styles.sidebar__desktop_scrim}
          aria-label={t('sideBar.closeMenu')}
          onClick={() => setDesktopOpen(false)}
        />
      ) : null}
      <div className={styles.sidebar__inner} aria-hidden={isMobile && !mobileOpen}>
        <nav className={styles['nav-bar']}>
          <div
            className={clx(styles.sidebar__header, {
              [styles.sidebar__header_empty]:
                isMobile && !isCalendar && !isStaff,
            })}
          >
            {isMobile ? (
              isCalendar ? (
                <SalonSelector isCompact={false} className={styles.sidebar__salon} />
              ) : null
            ) : (
              <SalonSelector isCompact={!desktopOpen} />
            )}
            {!isMobile && (
              <button
                type="button"
                className={clx(styles.sidebar__toggle, {
                  [styles.sidebar__toggle_open]: isExpanded,
                })}
                onClick={toggleSideBar}
                title={t('sideBar.toggleMenu')}
                aria-label={t('sideBar.toggleMenu')}
                aria-expanded={isExpanded}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="21"
                  height="21"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d={isExpanded ? 'M13 6l-5 6 5 6' : 'M9 6l5 6-5 6'} />
                </svg>
                <span className={styles.sidebar__toggle_label}>
                  {t('sideBar.closeMenu')}
                </span>
              </button>
            )}
            {isStaff && (
              <div
                className={styles.sidebar__staff}
                title={staffChairName || t('sideBar.staffAccount')}
              >
                <div className={styles.sidebar__staff_avatar}>
                  <AvatarIcon
                    icon={staffChairIcon}
                    name={staffChairName || t('sideBar.staffAccount')}
                    size={!isExpanded && !isMobile ? '40px' : '36px'}
                  />
                </div>
                <span className={styles.sidebar__staff_name_compact}>
                  {staffChairName || t('sideBar.staffAccount')}
                </span>
                <div className={styles.sidebar__staff_text}>
                  <span className={styles.sidebar__staff_label}>
                    {t('sideBar.staffAccount')}
                  </span>
                  <span className={styles.sidebar__staff_name}>
                    {staffChairName || '—'}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className={styles.navScroll}>
            {visibleNavGroups.map((group, groupIdx) => (
              <div key={`nav-group-${groupIdx}`} className={styles.navGroup}>
                {group.title ? (
                  <>
                    <div className={styles.navGroupTitle}>
                      <span className={styles.navGroupTitleText}>{group.title}</span>
                      <span className={styles.navGroupTitleLine} aria-hidden />
                    </div>
                    <div className={styles.navGroupDivider} aria-hidden />
                  </>
                ) : null}
                <ul>
                  {group.items.map((navItem) => (
                    <NavItem
                      key={navItem.link}
                      navItem={navItem}
                      activeItem={activeItem}
                      premiumLocked={Boolean(navItem.requiresPremium && !isPremiumUnlocked)}
                      onPremiumClick={goToUpgrade}
                      premiumLabel={t('statistics.premiumLock.title')}
                      showNewBadge={
                        Boolean(navItem.showNewBadge) &&
                        !(navItem.link === ROUTES.HOME && homeNewSeen)
                      }
                      newBadgeLabel={t('sideBar.badges.new')}
                      onNavigate={
                        isMobile ? closeMobile : () => setDesktopOpen(false)
                      }
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {isMobile ? (
            <ul className={styles.sidebar__footer_mobile}>
              <NavItemLogout
                onClick={() => {
                  setMobileOpen?.(false);
                  setSearchParams({ logout: 1 });
                  sendMessageToApp('app:logout');
                }}
              />
            </ul>
          ) : (
            <ul className={styles.sidebar__footer}>
              <NavItemLogout
                onClick={() => {
                  setSearchParams({ logout: 1 });
                  sendMessageToApp('app:logout');
                }}
              />
            </ul>
          )}
        </nav>
      </div>
      <LanguageSelectorModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
      />
    </aside>
  );
};

export default memo(SideBar);
